import * as amplitude from "@amplitude/analytics-browser";

import {
  BASE_AMPLITUDE_INIT_OPTIONS,
  createAmplitudeTracking,
  isSessionReplayEnabled as isReplayEnabled,
  normaliseUserId,
  parseSampleRate,
  type Outcome,
} from "shell/api/amplitudeCore";
import { SESSION_REPLAY_PRIVACY_CONFIG } from "shell/api/amplitudePrivacy";

const isSandboxStandaloneBuild =
  import.meta.env.VITE_BUILD_TARGET === "sandbox";
const enableFlag = import.meta.env.VITE_ENABLE_SANDBOX_AMPLITUDE;
const apiKey = import.meta.env.VITE_SANDBOX_AMPLITUDE_API_KEY;

export const isAmplitudeEnabled =
  isSandboxStandaloneBuild &&
  enableFlag?.toLowerCase() === "true" &&
  Boolean(apiKey);

const replayFlag = import.meta.env.VITE_ENABLE_SANDBOX_SESSION_REPLAY;
const replaySampleRate = parseSampleRate(
  import.meta.env.VITE_SANDBOX_SESSION_REPLAY_SAMPLE_RATE,
);

export const isSessionReplayEnabled =
  isAmplitudeEnabled &&
  isReplayEnabled(replayFlag?.toLowerCase() === "true", replaySampleRate);

let initialised = false;

// Loaded on demand to keep the recorder out of the entry bundle
const initSessionReplay = async () => {
  try {
    const { sessionReplayPlugin } =
      await import("@amplitude/plugin-session-replay-browser");

    await amplitude.add(
      sessionReplayPlugin({
        sampleRate: replaySampleRate,
        privacyConfig: SESSION_REPLAY_PRIVACY_CONFIG,
      }),
    ).promise;
  } catch (error) {
    console.warn("Failed to start Amplitude Session Replay", error);
  }
};

export const initAmplitude = () => {
  if (!isAmplitudeEnabled || initialised) return;

  initialised = true;

  amplitude.init(apiKey, {
    ...BASE_AMPLITUDE_INIT_OPTIONS,
    appVersion: APPLICATION_VERSION,
  });

  if (isSessionReplayEnabled) {
    void initSessionReplay();
  }
};

export const identifySandboxUser = (input: {
  company?: string;
  userId?: string | null;
}) => {
  if (!isAmplitudeEnabled) return;

  const userId = normaliseUserId(input.userId);
  if (!userId) return;

  amplitude.setUserId(userId);

  if (input.company) {
    amplitude.setGroup("company", input.company);
  }
};

export const resetAmplitude = () => {
  if (!isAmplitudeEnabled) return;

  amplitude.reset();
};

export type PasswordResetSource = "forgot_password" | "expired_link";

/**
 * Custom sandbox events.
 */
type SandboxEventMap = {
  "Signed In": Outcome;
  "Signed Up": Outcome & { country: string; marketing_consent: boolean };
  "Password Reset Requested": Outcome & { source: PasswordResetSource };
  "Password Reset Completed": Outcome;
  "Password Changed": Outcome;
  "Email Verified": Outcome;
  "Credential Created": Outcome;
  "Credential Deleted": Outcome;
  "Credential Secret Rotated": Outcome;
  [key: `API Request Sent - ${string}`]: Outcome & {
    duration_ms: number;
    endpoint_id: string;
    method: string;
    status: number;
  };
  "Feedback Submitted": Outcome;
  "Signed Out": Record<string, never>;
};

export type SandboxEvent = keyof SandboxEventMap;

export const trackAmplitudeEvent = <E extends SandboxEvent>(
  event: E,
  properties: SandboxEventMap[E],
) => {
  if (!isAmplitudeEnabled) return;

  amplitude.track(event, properties);
};

export const { withTracking } =
  createAmplitudeTracking<SandboxEventMap>(trackAmplitudeEvent);
